/**
 * @file Generateurs de positions de spawn par disposition (scatter, ring, grid, edge, noise, center) dans l'arene du mode.
 */
import type { Vec2 } from '@shared/types';
import type { ArenaDef, SpawnEntry, SpawnLayout } from '@config/types';
import type { Rng } from '../rng/Rng';

export interface LayoutRequest {
  layout: SpawnLayout;
  arena: ArenaDef;
  count: number;
  /** Distance minimale entre deux cibles et avec `avoid`. */
  minSpacing: number;
  /** Zones a eviter (position des chats, cibles existantes). */
  avoid: Array<Vec2 & { r: number }>;
  /** Rayon de l'anneau (ring) ; defaut 0.8 * rayon de l'arene. */
  ringRadius?: number;
  /** Echelle du bruit (noise). */
  noiseScale?: number;
}

/**
 * Specification (deterministe, uniquement via `rng`) :
 *  - 'scatter' : rejection sampling dans l'arene avec marge 1.5, respect de minSpacing et `avoid` ; max 30 essais par point,
 *                si echec on accepte le meilleur candidat (le plus eloigne).
 *  - 'ring'    : angles equirepartis + jitter rng.range(-0.15,0.15) rad, rayon ringRadius ; pour Monster, c'est le bord d'apparition.
 *  - 'grid'    : grille reguliere centree couvrant 80 % de l'arene, jitter +-0.4.
 *  - 'edge'    : points sur le contour de l'arene (rect : 4 cotes ponderes par longueur ; cercle : rayon - 0.5).
 *  - 'noise'   : comme scatter mais acceptation proportionnelle a un bruit de valeur 2D (hash de cellule interpole lisse,
 *                echelle noiseScale) pour former des filons (mode Mine, Mine_manager "placement par bruit").
 *  - 'center'  : [{x:0,y:0}] puis spirale de rayon minSpacing.
 *  - 'none'    : [].
 */
export const layoutPositions = (rng: Rng, req: LayoutRequest): Vec2[] => {
  switch (req.layout) {
    case 'none':
      return [];
    case 'scatter':
      return sample(rng, req, () => 1);
    case 'noise': {
      const seed = rng.nextU32();
      const scale = req.noiseScale ?? 0.18;
      return sample(rng, req, (x, y) => Math.pow(valueNoise(seed, x * scale, y * scale), 1.5));
    }
    case 'ring':
      return ring(rng, req);
    case 'grid':
      return grid(rng, req);
    case 'edge':
      return edge(rng, req);
    case 'center':
      return center(req);
  }
};

const MARGIN = 1.5;

/** Rayon utile de l'arene (cercle : halfW ; rect : plus petit demi-cote). */
const arenaRadius = (a: ArenaDef): number => (a.shape === 'circle' ? a.halfW : Math.min(a.halfW, a.halfH));

const randomInArena = (rng: Rng, a: ArenaDef, margin: number): Vec2 => {
  if (a.shape === 'circle') {
    const r = Math.max(0, a.halfW - margin) * Math.sqrt(rng.next());
    const t = rng.range(0, Math.PI * 2);
    return { x: Math.cos(t) * r, y: Math.sin(t) * r };
  }
  return { x: rng.range(-a.halfW + margin, a.halfW - margin), y: rng.range(-a.halfH + margin, a.halfH - margin) };
};

/** Distance minimale (normalisee par l'ecart requis) a l'ensemble des points deja places et des zones a eviter. */
const clearance = (p: Vec2, placed: Vec2[], req: LayoutRequest): number => {
  let best = Number.POSITIVE_INFINITY;
  for (const q of placed) best = Math.min(best, Math.hypot(p.x - q.x, p.y - q.y) - req.minSpacing);
  for (const a of req.avoid) best = Math.min(best, Math.hypot(p.x - a.x, p.y - a.y) - (a.r + req.minSpacing * 0.5));
  return best;
};

/** Rejection sampling : `weight(x,y)` in [0,1] = probabilite d'acceptation (1 = scatter pur). */
const sample = (rng: Rng, req: LayoutRequest, weight: (x: number, y: number) => number): Vec2[] => {
  const out: Vec2[] = [];
  for (let i = 0; i < req.count; i++) {
    let best: Vec2 | null = null;
    let bestScore = Number.NEGATIVE_INFINITY;
    let accepted: Vec2 | null = null;
    for (let t = 0; t < 30; t++) {
      const c = randomInArena(rng, req.arena, MARGIN);
      const clr = clearance(c, out, req);
      if (clr >= 0 && rng.next() < weight(c.x, c.y)) {
        accepted = c;
        break;
      }
      // Repli : meilleur candidat (le plus eloigne), pondere par la qualite du bruit.
      const score = clr + weight(c.x, c.y);
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    out.push(accepted ?? best ?? { x: 0, y: 0 });
  }
  return out;
};

const ring = (rng: Rng, req: LayoutRequest): Vec2[] => {
  const r = req.ringRadius ?? arenaRadius(req.arena) * 0.8;
  const start = rng.range(0, Math.PI * 2);
  const out: Vec2[] = [];
  for (let i = 0; i < req.count; i++) {
    const a = start + (i / Math.max(1, req.count)) * Math.PI * 2 + rng.range(-0.15, 0.15);
    out.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return out;
};

const grid = (rng: Rng, req: LayoutRequest): Vec2[] => {
  const a = req.arena;
  const w = (a.shape === 'circle' ? a.halfW : a.halfW) * 2 * 0.8;
  const h = (a.shape === 'circle' ? a.halfW : a.halfH) * 2 * 0.8;
  const n = Math.max(1, req.count);
  const cols = Math.max(1, Math.ceil(Math.sqrt((n * w) / h)));
  const rows = Math.max(1, Math.ceil(n / cols));
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const cx = i % cols;
    const cy = Math.floor(i / cols);
    const x = cols === 1 ? 0 : -w / 2 + (w * cx) / (cols - 1);
    const y = rows === 1 ? 0 : -h / 2 + (h * cy) / (rows - 1);
    out.push({ x: x + rng.range(-0.4, 0.4), y: y + rng.range(-0.4, 0.4) });
  }
  return out;
};

const edge = (rng: Rng, req: LayoutRequest): Vec2[] => {
  const a = req.arena;
  const out: Vec2[] = [];
  for (let i = 0; i < req.count; i++) {
    if (a.shape === 'circle') {
      const t = rng.range(0, Math.PI * 2);
      const r = a.halfW - 0.5;
      out.push({ x: Math.cos(t) * r, y: Math.sin(t) * r });
      continue;
    }
    const w = a.halfW - 0.5;
    const h = a.halfH - 0.5;
    const perimeter = 4 * (w + h);
    let u = rng.range(0, perimeter);
    // Sides ponderes par leur longueur : haut/bas = 2w, gauche/droite = 2h.
    if (u < 2 * w) out.push({ x: -w + u, y: h });
    else if ((u -= 2 * w) < 2 * h) out.push({ x: w, y: h - u });
    else if ((u -= 2 * h) < 2 * w) out.push({ x: w - u, y: -h });
    else out.push({ x: -w, y: -h + (u - 2 * w) });
  }
  return out;
};

const center = (req: LayoutRequest): Vec2[] => {
  const out: Vec2[] = [];
  const golden = 2.399963229728653;
  for (let i = 0; i < req.count; i++) {
    if (i === 0) {
      out.push({ x: 0, y: 0 });
      continue;
    }
    const r = req.minSpacing * Math.sqrt(i);
    out.push({ x: Math.cos(i * golden) * r, y: Math.sin(i * golden) * r });
  }
  return out;
};

// ------------------------------------------------------------------ bruit de valeur 2D (hash de cellule interpole lisse)

const hash2 = (seed: number, ix: number, iy: number): number => {
  let h = (seed ^ Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iy, 0x165667b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

const smooth = (t: number): number => t * t * (3 - 2 * t);

const valueNoise = (seed: number, x: number, y: number): number => {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = smooth(x - ix);
  const fy = smooth(y - iy);
  const a = hash2(seed, ix, iy);
  const b = hash2(seed, ix + 1, iy);
  const c = hash2(seed, ix, iy + 1);
  const d = hash2(seed, ix + 1, iy + 1);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
};

/** Tirage pondere d'une entree de table de spawn active pour la valeur `value` (minValue/maxValue). */
export const pickSpawnEntry = (rng: Rng, table: readonly SpawnEntry[], value: number): SpawnEntry | null =>
  rng.weighted(
    table.filter((e) => (e.minValue === undefined || value >= e.minValue) && (e.maxValue === undefined || value <= e.maxValue)),
    (e) => e.weight,
  );
